import { type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'motion/react';
import { DEFAULT_ANIMATION_DURATION } from '@/config';
import IcShield from '@/assets/icons/Shield.svg?react';
import IcIdCard from '@/assets/icons/IdCard.svg?react';
import IcLock from '@/assets/icons/Lock.svg?react';
import Button from '@/components/Button';
import FormSelect from '@/components/FormSelect';
import { supportedProviders } from '@/data/kycProviders';
import styles from './MethodSelection.module.scss';

export type KycMethod = 'existing' | 'new';

interface MethodSelectionProps {
  /** Currently selected method card, or null when nothing is picked yet. */
  method: KycMethod | null;
  /** Provider code chosen in the eID dropdown ('none' = not yet chosen). */
  selectedProvider: string;
  loading: boolean;
  errorMessage?: string;
  onMethodChange: (method: KycMethod) => void;
  onProviderChange: (provider: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

const MethodSelection = ({
  method,
  selectedProvider,
  loading,
  errorMessage,
  onMethodChange,
  onProviderChange,
  onSubmit,
}: MethodSelectionProps) => {
  const { t } = useTranslation(['onboarding']);

  // 'none' is the placeholder code; only real providers count as a selection.
  const providerChosen = selectedProvider !== 'none';
  const continueDisabled =
    loading || method === null || (method === 'existing' && !providerChosen);

  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <h2 className={styles.title}>
          {t('onboarding:kycRedesign.method.question')}
        </h2>
        <p className={styles.subtitle}>
          {t('onboarding:kycRedesign.method.questionHint')}
        </p>
      </header>

      <form className={styles.form} onSubmit={onSubmit} noValidate>
        <label
          className={styles.methodCard}
          data-selected={method === 'existing'}
        >
          <input
            type="radio"
            name="kyc-method"
            className={styles.radioInput}
            aria-label={t('onboarding:kycRedesign.method.existing.title')}
            checked={method === 'existing'}
            onChange={() => onMethodChange('existing')}
            disabled={loading}
          />
          <span className={styles.methodRow}>
            <span className={styles.methodIcon} aria-hidden="true">
              <IcShield />
            </span>
            <span className={styles.methodBody}>
              <span className={styles.methodHead}>
                <span className={styles.methodTitle}>
                  {t('onboarding:kycRedesign.method.existing.title')}
                </span>
                <span className={styles.badge}>
                  {t('onboarding:kycRedesign.method.existing.badge')}
                </span>
              </span>
              <span className={styles.methodDesc}>
                {t('onboarding:kycRedesign.method.existing.desc')}
              </span>
            </span>
            <span className={styles.radioDot} aria-hidden="true" />
          </span>

          {/* Provider picker lives inside the eID bubble, revealed on select. */}
          <AnimatePresence initial={false}>
            {method === 'existing' && (
              <motion.span
                className={styles.providerPanel}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: DEFAULT_ANIMATION_DURATION }}
              >
                <span className={styles.providerInner}>
                  <FormSelect
                    id="kyc-provider"
                    label={t(
                      'onboarding:kycRedesign.method.existing.selectLabel'
                    )}
                    value={selectedProvider}
                    onChange={(event) =>
                      onProviderChange(event.currentTarget.value)
                    }
                    disabled={loading}
                    errorMessage={errorMessage}
                  >
                    {supportedProviders.map((provider) => (
                      <option key={provider.code} value={provider.code}>
                        {provider.code === 'none'
                          ? t(
                              'onboarding:kycRedesign.method.existing.selectPlaceholder'
                            )
                          : provider.name}
                      </option>
                    ))}
                  </FormSelect>
                  <span className={styles.providerHint}>
                    <IcLock className={styles.hintIcon} aria-hidden="true" />
                    {t('onboarding:kycRedesign.method.existing.providerHint')}
                  </span>
                </span>
              </motion.span>
            )}
          </AnimatePresence>
        </label>

        <div className={styles.divider} aria-hidden="true">
          <span>{t('onboarding:kycRedesign.method.noProvider')}</span>
        </div>

        <label className={styles.methodCard} data-selected={method === 'new'}>
          <input
            type="radio"
            name="kyc-method"
            className={styles.radioInput}
            aria-label={t('onboarding:kycRedesign.method.new.title')}
            checked={method === 'new'}
            onChange={() => onMethodChange('new')}
            disabled={loading}
          />
          <span className={styles.methodRow}>
            <span className={styles.methodIcon} aria-hidden="true">
              <IcIdCard />
            </span>
            <span className={styles.methodBody}>
              <span className={styles.methodHead}>
                <span className={styles.methodTitle}>
                  {t('onboarding:kycRedesign.method.new.title')}
                </span>
              </span>
              <span className={styles.newBadge}>
                {t('onboarding:kycRedesign.method.new.badge')}
              </span>
              <span className={styles.methodDesc}>
                {t('onboarding:kycRedesign.method.new.desc')}
              </span>
            </span>
            <span className={styles.radioDot} aria-hidden="true" />
          </span>
        </label>

        <footer className={styles.footer}>
          <span className={styles.secure}>
            <IcLock className={styles.secureIcon} aria-hidden="true" />
            {t('onboarding:kycRedesign.footer.secure')}
          </span>
          <Button submit loading={loading} disabled={continueDisabled}>
            {t('onboarding:kycSelection.continue')}
          </Button>
        </footer>
      </form>
    </section>
  );
};

export default MethodSelection;
